import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { fetchMyNotes, createNote, updateNote, deleteNote, fetchThreads, createThread } from "@/lib/api";
import { NotebookPen, MessageCircleQuestion, MessagesSquare, Trash2, Lock, Globe, Plus, X } from "lucide-react";
import { toast } from "sonner";

// Unified per-page engagement: private notes + attached Questions & Discussions.
// Used on content / deity / temple / knowledge-hub detail pages.
export default function PageEngage({ refType, refSlug, refTitle }) {
  const { user } = useAuth();
  const { pick } = useLang();
  const en = pick("x", "y") === "y";

  return (
    <section className="mx-auto max-w-5xl px-5 py-12 md:px-8" data-testid="page-engage">
      <div className="grid gap-10 lg:grid-cols-2">
        <NotesPanel user={user} en={en} refType={refType} refSlug={refSlug} refTitle={refTitle} />
        <ThreadsPanel user={user} en={en} refType={refType} refSlug={refSlug} refTitle={refTitle} />
      </div>
    </section>
  );
}

function NotesPanel({ user, en, refType, refSlug, refTitle }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [vis, setVis] = useState("private");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    fetchMyNotes(refSlug).then(setNotes).catch(() => {});
  }, [user, refSlug]);

  const add = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      const n = await createNote({ ref_type: refType, ref_slug: refSlug, ref_title: refTitle, body, visibility: vis });
      setNotes((p) => [n, ...p]);
      setBody("");
      toast(en ? "Note saved" : "टिप्पणी सहेजी गई");
    } catch (e) { toast.error(en ? "Could not save note" : "सहेज नहीं सके", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await deleteNote(id); setNotes((p) => p.filter((n) => n.id !== id)); } catch { /* ignore */ }
  };
  const toggleVis = async (n) => {
    const nv = n.visibility === "public" ? "private" : "public";
    try { const u = await updateNote(n.id, { ref_type: n.ref_type, ref_slug: n.ref_slug, body: n.body, visibility: nv }); setNotes((p) => p.map((x) => x.id === n.id ? u : x)); } catch { /* ignore */ }
  };

  return (
    <div data-testid="notes-panel">
      <h3 className="mb-1 flex items-center gap-2 font-serif-latin text-2xl text-ink"><NotebookPen className="h-5 w-5 text-saffron" /> {en ? "Your notes" : "आपकी टिप्पणियाँ"}</h3>
      <p className="mb-4 text-sm text-ink-3">{en ? "Private reflections you can revisit anytime." : "आपके लिए सुरक्षित निजी टिप्पणियाँ।"}</p>
      {!user ? (
        <div className="border border-line bg-surface p-6 text-sm text-ink-2">
          {en ? "Sign in to take and save personal notes on this page." : "इस पृष्ठ पर टिप्पणी सहेजने हेतु साइन इन करें।"}{" "}
          <Link to="/login" className="text-saffron hover:underline">{en ? "Sign in" : "साइन इन"}</Link>
        </div>
      ) : (
        <>
          <div className="border border-line bg-surface p-4">
            <textarea data-testid="note-input" value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={en ? "Write a note…" : "एक टिप्पणी लिखें…"} className="w-full resize-none border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            <div className="mt-2 flex items-center justify-between">
              <button onClick={() => setVis((v) => v === "public" ? "private" : "public")} className="inline-flex items-center gap-1.5 text-xs text-ink-3 hover:text-ink-2" data-testid="note-visibility">
                {vis === "public" ? <><Globe className="h-3.5 w-3.5" /> {en ? "Shared" : "साझा"}</> : <><Lock className="h-3.5 w-3.5" /> {en ? "Private" : "निजी"}</>}
              </button>
              <button data-testid="note-save" onClick={add} disabled={busy || !body.trim()} className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-50"><Plus className="h-4 w-4" /> {en ? "Save note" : "सहेजें"}</button>
            </div>
          </div>
          <ul className="mt-4 space-y-3">
            {notes.map((n) => (
              <li key={n.id} data-testid={`note-${n.id}`} className="group border border-line bg-surface p-4">
                <p className="whitespace-pre-line text-sm leading-relaxed text-ink-2">{n.body}</p>
                <div className="mt-2 flex items-center justify-between text-xs text-ink-3">
                  <button onClick={() => toggleVis(n)} className="inline-flex items-center gap-1 hover:text-ink-2">{n.visibility === "public" ? <><Globe className="h-3 w-3" /> {en ? "Shared" : "साझा"}</> : <><Lock className="h-3 w-3" /> {en ? "Private" : "निजी"}</>}</button>
                  <button onClick={() => remove(n.id)} className="inline-flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 hover:text-[var(--saffron-dark)]"><Trash2 className="h-3 w-3" /> {en ? "Delete" : "हटाएँ"}</button>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ThreadsPanel({ user, en, refType, refSlug, refTitle }) {
  const [threads, setThreads] = useState([]);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("qna");
  const [title, setTitle] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [guestName, setGuestName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => fetchThreads({ ref_slug: refSlug }).then(setThreads).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [refSlug]);

  const submit = async () => {
    if (!title.trim()) { toast.error(en ? "Add a title" : "शीर्षक जोड़ें"); return; }
    setBusy(true);
    try {
      await createThread({ kind, title, body: bodyText, ref_type: refType, ref_slug: refSlug, ref_title: refTitle, guest_name: user ? undefined : guestName });
      setTitle(""); setBodyText(""); setOpen(false);
      toast(en ? "Posted — thank you!" : "प्रकाशित — धन्यवाद!");
      load();
    } catch (e) { toast.error(en ? "Could not post" : "प्रकाशित नहीं कर सके", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="threads-panel">
      <h3 className="mb-1 flex items-center gap-2 font-serif-latin text-2xl text-ink"><MessagesSquare className="h-5 w-5 text-saffron" /> {en ? "Questions & Discussions" : "प्रश्न एवं चर्चाएँ"}</h3>
      <p className="mb-4 text-sm text-ink-3">{en ? "Ask a question or start a discussion about this page." : "इस पृष्ठ पर प्रश्न पूछें या चर्चा आरंभ करें।"}</p>

      {!open ? (
        <div className="flex flex-wrap gap-2">
          <button data-testid="ask-question-btn" onClick={() => { setKind("qna"); setOpen(true); }} className="inline-flex items-center gap-1.5 border border-line px-4 py-2.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron"><MessageCircleQuestion className="h-4 w-4" /> {en ? "Ask a question" : "प्रश्न पूछें"}</button>
          <button data-testid="start-discussion-btn" onClick={() => { setKind("discussion"); setOpen(true); }} className="inline-flex items-center gap-1.5 border border-line px-4 py-2.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron"><MessagesSquare className="h-4 w-4" /> {en ? "Start a discussion" : "चर्चा आरंभ करें"}</button>
        </div>
      ) : (
        <div className="border border-line bg-surface p-4" data-testid="engage-composer">
          <div className="mb-2 flex items-center justify-between">
            <span className="overline-label text-gold">{kind === "qna" ? (en ? "Ask a question" : "प्रश्न") : (en ? "Start a discussion" : "चर्चा")}</span>
            <button onClick={() => setOpen(false)} className="text-ink-3 hover:text-ink"><X className="h-4 w-4" /></button>
          </div>
          {!user && (
            <input data-testid="engage-guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder={en ? "Your name (posting as guest)" : "आपका नाम (अतिथि)"} className="mb-2 w-full border border-line bg-parchment px-3 py-2 text-sm focus:border-saffron focus:outline-none" />
          )}
          <input data-testid="engage-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={kind === "qna" ? (en ? "Your question…" : "आपका प्रश्न…") : (en ? "Discussion title…" : "चर्चा शीर्षक…")} className="mb-2 w-full border border-line bg-parchment px-3 py-2 text-sm focus:border-saffron focus:outline-none" />
          <textarea data-testid="engage-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={3} placeholder={en ? "Add details (optional)" : "विवरण (वैकल्पिक)"} className="mb-2 w-full resize-none border border-line bg-parchment px-3 py-2 text-sm focus:border-saffron focus:outline-none" />
          <div className="flex justify-end">
            <button data-testid="engage-submit" onClick={submit} disabled={busy} className="bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-50">{busy ? "…" : (en ? "Post" : "प्रकाशित करें")}</button>
          </div>
        </div>
      )}

      <ul className="mt-4 space-y-2">
        {threads.map((t) => (
          <li key={t.id} data-testid={`engage-thread-${t.id}`}>
            <Link to={`${t.kind === "qna" ? "/qna" : "/discussions"}/${t.id}`} className="block border border-line bg-surface px-4 py-3 transition-colors hover:border-saffron">
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${t.kind === "qna" ? "bg-saffron/15 text-saffron" : "bg-ink/10 text-ink-2"}`}>{t.kind === "qna" ? (en ? "Question" : "प्रश्न") : (en ? "Discussion" : "चर्चा")}</span>
                <span className="min-w-0 flex-1 truncate text-sm text-ink">{t.title}</span>
              </div>
              <p className="mt-1 text-xs text-ink-3">{t.author_name} · {t.reply_count || 0} {en ? "replies" : "उत्तर"}</p>
            </Link>
          </li>
        ))}
        {threads.length === 0 && <li className="py-6 text-center text-sm text-ink-3">{en ? "No questions or discussions yet — be the first." : "अभी कोई प्रश्न या चर्चा नहीं — पहले आप आरंभ करें।"}</li>}
      </ul>
    </div>
  );
}
